import type { FlattenedToken, PARENToken, ParsedToken } from '../../../types/QueryBuilder';

/**
 * Recursively flatten EXPRESSION tokens into PAREN open/close pairs surrounding
 * their children. All other token types pass through unchanged.
 */
export function flattenTokens(tokens: ParsedToken[]): FlattenedToken[] {
  return tokens.flatMap((token): FlattenedToken[] => {
    if (token.type !== 'EXPRESSION') {
      return [token];
    }
    const open: PARENToken = { type: 'PAREN', side: 'open', expression: token };
    const close: PARENToken = { type: 'PAREN', side: 'close', expression: token };
    return [open, ...flattenTokens(token.inner), close];
  });
}

/**
 * Recursively collect every `expressionType` already present in the parsed query.
 * Used by LogicParen / FreeTextToken to disable relational-filter autocomplete
 * options that are already in use elsewhere in the query.
 */
export function collectUsedExpressionTypes(tokens: ParsedToken[]): Set<string> {
  const set = new Set<string>();
  for (const t of tokens) {
    if (t.type === 'EXPRESSION') {
      if (t.expressionType) {
        set.add(t.expressionType);
      }
      for (const inner of collectUsedExpressionTypes(t.inner)) {
        set.add(inner);
      }
    }
  }
  return set;
}

/**
 * Maps a set of selected grid keys back to their underlying ParsedTokens.
 * Keys prefixed with "ft-" are auto-inserted FREE_TEXT boundaries with no ParsedToken.
 * PAREN tokens are resolved to their parent EXPRESSION token (with deduplication).
 */
export function getSelectedParsedTokens(selectedKeys: Iterable<string>, flatTokens: FlattenedToken[]): ParsedToken[] {
  const seen = new Set<ParsedToken>();
  const result: ParsedToken[] = [];
  for (const key of selectedKeys) {
    if (key.startsWith('ft-')) {
      continue;
    }
    const i = parseInt(key, 10);
    if (isNaN(i)) {
      continue;
    }
    const flatToken = flatTokens[i];
    if (!flatToken) {
      continue;
    }
    const parsedToken = flatToken.type === 'PAREN' ? flatToken.expression : (flatToken as ParsedToken);
    if (!seen.has(parsedToken)) {
      seen.add(parsedToken);
      result.push(parsedToken);
    }
  }
  return result;
}
